import {
  useLocation,
  useRouteLoaderData,
  useSearchParams,
} from "@remix-run/react";
import { useTranslation } from "react-i18next";

import Button from "~/components/atoms/Button";
import InputRange from "~/components/atoms/InputRange/InputRange";
import MultiSelect from "~/components/atoms/SelectBox/MultiSelect";
import Typo from "~/components/atoms/Typo";
import { Form, TFormProps } from "~/components/templates/form";
import { IRouteLoaderData } from "~/types/routeLoaderData";
import { cn } from "~/utilities/cn";
import { OPTION_ANY } from "~/utilities/constants/common";
import { ETypoColor } from "~/utilities/enums/Colors";
import { EMarketSegment } from "~/utilities/enums/marketSegment";
import { EProjectStatus } from "~/utilities/enums/ProjectStatus";
import { ETypoTag, ETypoVariant } from "~/utilities/enums/Typo";
import {
  filterSideBarSchema,
  TFilterSideBarFormData,
} from "~/utilities/schema/filterSideBar";

export interface IFilterSideBarProps {
  defaultValues?: TFilterSideBarFormData;
  mode?: TFormProps<any>["submitMode"];
  onClose?: () => void;
}

const FilterSideBar: React.FC<IFilterSideBarProps> = ({
  defaultValues,
  mode = "onChange",
  onClose,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { filterOptions } = useRouteLoaderData("root") as IRouteLoaderData;

  const unitSize = filterOptions?.area;

  const psfs = filterOptions?.psf;

  const getParamArray = (param: string | null): string[] =>
    param ? param.split(",") : [];

  const defaultFormValues: Partial<TFilterSideBarFormData> = {
    marketSegment: getParamArray(searchParams.get("marketSegment")),
    projectType: getParamArray(searchParams.get("projectType")),
    status: getParamArray(searchParams.get("status")),
    unitSizeMax: searchParams.get("unitSizeMax") || "0",
    unitSizeMin: searchParams.get("unitSizeMin") || "0",
    unitPSFMin: searchParams.get("unitPSFMin") || "0",
    unitPSFMax: searchParams.get("unitPSFMax") || "0",
    ...defaultValues,
  };

  const MARKET_SEGMENT_OPTIONS = {
    name: "marketSegment",
    label: "marketSegment",
    placeholder: "marketSegment_placeholder",
    options: [
      { id: EMarketSegment.CCR, name: t(EMarketSegment.CCR) },
      { id: EMarketSegment.OCR, name: t(EMarketSegment.OCR) },
      { id: EMarketSegment.RCR, name: t(EMarketSegment.RCR) },
    ],
  };

  const PROJECT_TYPE_OPTIONS = {
    name: "projectType",
    label: "projectType",
    placeholder: "projectType_placeholder",
    options: filterOptions?.categories,
  };

  const STATUS_OPTIONS = {
    name: "status",
    label: "status",
    placeholder: "status_placeholder",
    options: [
      { id: EProjectStatus.BY_BUC, name: t(EProjectStatus.BY_BUC) },
      { id: EProjectStatus.TOP_SOON, name: t(EProjectStatus.TOP_SOON) },
      { id: EProjectStatus.COMPLETED, name: t(EProjectStatus.COMPLETED) },
    ],
  };

  const location = useLocation();

  const getDefaultSelectMultipleValue = ({
    ids,
    options,
  }: {
    ids?: string[];
    options: { id: string; name: string }[];
  }) => {
    if (!ids || !ids?.length || !Array.isArray(ids)) return [];

    return options.filter((option) => ids.includes(option.id));
  };

  return (
    <>
      <Form<TFilterSideBarFormData>
        validationSchema={filterSideBarSchema}
        useFormProps={{ mode: mode, defaultValues: defaultFormValues }}
        submitMode={mode}
        action={location.pathname + location.search}
        className="w-full"
      >
        {({ register, formState: { errors } }) => (
          <div
            className={cn(
              "filter-bar-sub-container w-full",
              "lg:no-scrollbar flex flex-1 flex-row flex-nowrap items-end justify-between gap-x-7 gap-y-3 rounded-3xl bg-filterBarBackground p-6 lg:w-full lg:max-w-full lg:flex-col lg:overflow-auto lg:rounded-lg lg:px-6 lg:py-3"
            )}
          >
            <MultiSelect
              data={MARKET_SEGMENT_OPTIONS?.options}
              name={MARKET_SEGMENT_OPTIONS?.name}
              placeholder={t(MARKET_SEGMENT_OPTIONS?.placeholder)}
              title={t(MARKET_SEGMENT_OPTIONS?.label)}
              dropdownClass="w-[var(--button-width)]"
              containerClass="flex-1 lg:w-full"
              textSelectedClass="font-normal"
              placeholderClass="font-normal"
              defaultValue={getDefaultSelectMultipleValue({
                ids: defaultFormValues.marketSegment,
                options: MARKET_SEGMENT_OPTIONS?.options,
              })}
            />

            <MultiSelect
              data={PROJECT_TYPE_OPTIONS?.options}
              name={PROJECT_TYPE_OPTIONS?.name}
              placeholder={t(PROJECT_TYPE_OPTIONS?.placeholder)}
              title={t(PROJECT_TYPE_OPTIONS?.label)}
              dropdownClass="scroll w-[var(--button-width)]"
              containerClass="flex-1 lg:w-full"
              textSelectedClass="font-normal"
              placeholderClass="font-normal"
              defaultValue={getDefaultSelectMultipleValue({
                ids: defaultFormValues?.projectType,
                options: PROJECT_TYPE_OPTIONS?.options,
              })}
            />

            <MultiSelect
              data={STATUS_OPTIONS?.options}
              name={STATUS_OPTIONS?.name}
              placeholder={t(STATUS_OPTIONS?.placeholder)}
              title={t(STATUS_OPTIONS?.label)}
              dropdownClass="w-[var(--button-width)]"
              containerClass="flex-1 lg:w-full"
              textSelectedClass="font-normal"
              placeholderClass="font-normal"
              defaultValue={getDefaultSelectMultipleValue({
                ids: defaultFormValues?.status,
                options: STATUS_OPTIONS?.options,
              })}
            />

            <div className="flex-1 lg:w-full">
              <div className="flex h-full basis-[30%] flex-col gap-3 lg:w-full lg:min-w-[100px]">
                <InputRange
                  errors={{
                    maxErrors: errors.unitSizeMax?.message,
                    minErrors: errors.unitSizeMin?.message,
                  }}
                  minValueSelect={{
                    placeholder: t("min"),
                    error: errors.unitSizeMin?.message,
                    data: unitSize,
                    title: t("min_size"),
                    name: "unitSizeMin",
                    defaultValue: [OPTION_ANY, ...(unitSize || [])]?.find(
                      (l) => l.id === searchParams.get("unitSizeMin") || ""
                    ),
                  }}
                  maxValueSelect={{
                    placeholder: t("max"),
                    error: errors.unitSizeMax?.message,
                    data: unitSize,
                    title: t("max_size"),
                    name: "unitSizeMax",
                    defaultValue: [OPTION_ANY, ...(unitSize || [])]?.find(
                      (l) => l.id === searchParams.get("unitSizeMax") || ""
                    ),
                  }}
                  title={{
                    text: t("unit_size"),
                    fontSize: ETypoVariant.SUB_TITLE_20,
                    className: "mb-4.5",
                  }}
                  variant="select"
                />
              </div>
            </div>
            <div className="flex-1 lg:w-full">
              <InputRange
                errors={{
                  maxErrors: errors.unitPSFMax?.message,
                  minErrors: errors.unitPSFMin?.message,
                }}
                minValueSelect={{
                  placeholder: t("min"),
                  error: errors.unitPSFMin?.message,
                  data: psfs,
                  title: t("min"),
                  name: "unitPSFMin",
                  defaultValue: [OPTION_ANY, ...(unitSize || [])]?.find(
                    (l) => l.id === searchParams.get("unitPSFMin") || ""
                  ),
                }}
                maxValueSelect={{
                  placeholder: t("max"),
                  error: errors.unitPSFMax?.message,
                  data: psfs,
                  title: t("max"),
                  name: "unitPSFMax",
                  defaultValue: [OPTION_ANY, ...(unitSize || [])]?.find(
                    (l) => l.id === searchParams.get("unitPSFMax") || ""
                  ),
                }}
                title={{
                  text: t("unit_PSF"),
                  fontSize: ETypoVariant.SUB_TITLE_20,
                  className: "mb-4.5",
                }}
                variant="select"
              />
            </div>
            <Button
              type="submit"
              className="flex-0 mb-1 border-none lg:mx-auto lg:px-13"
              onClick={onClose}
            >
              <Typo
                className="whitespace-nowrap font-normal capitalize"
                tag={ETypoTag.SPAN}
                variant={ETypoVariant.BODY_TITLE_16}
                color={ETypoColor.WHITE}
              >
                {t("filter")}
              </Typo>
            </Button>
          </div>
        )}
      </Form>
    </>
  );
};

export default FilterSideBar;
